import { Input, OnInit, OnDestroy, ViewChild, Output, EventEmitter, Directive, HostBinding, ElementRef, Optional, Self, DoCheck } from '@angular/core';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { ResultFilter, Results } from '../services/platform-api.service';
import { Subject, Observable, BehaviorSubject } from 'rxjs';
import { tap, takeUntil, debounceTime, map, scan, filter } from 'rxjs/operators';
import { FormControl, NgControl, ControlValueAccessor } from '@angular/forms';
import { MatAutocomplete } from '@angular/material/autocomplete';
import { MatFormFieldControl } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { GridSpinnerComponent } from './grid-spinner.component';
import { FocusMonitor } from '@angular/cdk/a11y';

export interface IAutoCompleteScrollEvent {
  autoComplete: MatAutocomplete;
  scrollEvent: Event;
}

@Directive({
  selector: 'mat-autocomplete[cwInfiniteScroll]'
})
export class AutoCompleteInfiniteScrollDirective implements OnDestroy {
  @Input() thresholdPercent = .8;
  @Output() cwInfiniteScroll = new EventEmitter<IAutoCompleteScrollEvent>();
  _onDestroy = new Subject();

  constructor(public autoComplete: MatAutocomplete) {
    this.autoComplete.opened.pipe(
      debounceTime(0),
      tap(() => {
        // Note: When autocomplete raises opened, panel is not yet created (by Overlay)
        // Note: The panel will be available on next tick
        // Note: The panel wil NOT open if there are no options to display
          this.removeScrollEventListener();
          if(this.autoComplete.panel) {
            this.autoComplete.panel.nativeElement.getElementsByClassName('scroll-area')[0]
              .addEventListener('scroll', this.onScroll.bind(this))
          }
      }),
      takeUntil(this._onDestroy)).subscribe();

    this.autoComplete.closed.pipe(
      tap(() => this.removeScrollEventListener()),
      takeUntil(this._onDestroy)).subscribe();
  }

  private removeScrollEventListener() {
    if(this.autoComplete.panel) {
      this.autoComplete.panel.nativeElement.removeEventListener('scroll', this.onScroll);
    }
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();

    this.removeScrollEventListener();
  }

  onScroll(event: Event) {

    if (this.thresholdPercent === undefined) {
      this.cwInfiniteScroll.next({ autoComplete: this.autoComplete, scrollEvent: event });
    } else {
      const target = event.target as Element;
      const threshold = this.thresholdPercent * 100 * target.scrollHeight / 100;
      const current = target.scrollTop + target.clientHeight;

      if (current > threshold) {
        this.cwInfiniteScroll.next({ autoComplete: this.autoComplete, scrollEvent: event });
      }
    }
  }
}

export class InfiniteScrollOption<T> {
  public description: string;
  public value:T;
  public disabled?:boolean;
  public data:{};
  public selected?:boolean;
  public isValid?:boolean;

  constructor(description: string, value: T, disabled: boolean, selected: boolean, isValid: boolean, data?: {}) {
    this.description = description;
    this.value = value;
    this.disabled = disabled;
    this.data = data;
    this.selected = selected;
    this.isValid = isValid;
  }
}

@Directive()
export abstract class InfiniteScrollSelectComponent<T> implements OnInit, OnDestroy, DoCheck, MatFormFieldControl<InfiniteScrollOption<T>>, ControlValueAccessor {
  @Input()
  get placeholder(): string {
    return this._placeholder;
  }
  set placeholder(plh: string) {
    this._placeholder = plh;
    this.stateChanges.next();
  }
  @Input()
  get value(): InfiniteScrollOption<T> {
    return this.searchControl.value instanceof InfiniteScrollOption ? this.searchControl.value : null;
  }
  set value(option: InfiniteScrollOption<T> | null) {
    this.searchControl.setValue(option);
    this.stateChanges.next();
  }
  get empty() {
    return !(this.searchControl.value instanceof InfiniteScrollOption);
  }
  @Input()
  get required(): boolean {
    return this._required;
  }
  set required(req: boolean) {
    this._required = coerceBooleanProperty(req);
    this.stateChanges.next();
  }
  @Input()
  get disabled(): boolean { return this._disabled; }
  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
    this.stateChanges.next();
  }


  @HostBinding('class.floating')
  get shouldLabelFloat() {
    return this.focused || this.searchControl.value;
  }

  constructor(private fm: FocusMonitor, private elRef: ElementRef<HTMLElement>, @Optional() @Self() public ngControl: NgControl) {
    this.options$ = this.options.asObservable().pipe(
      scan((acc, curr) => {
        if(this.clearResults) {
          this.clearResults = false;
          return curr;
        } else {
          return [...acc, ...curr];
        }
      }, [])
    );

    fm.monitor(elRef.nativeElement, true).subscribe(origin => {
      if(this.focused && !origin) {
        this.onTouched();
      }
      this.focused = !!origin;
      this.stateChanges.next();
    });

    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }
  static nextId = 0;
  @ViewChild('auto', {static: true}) auto: MatAutocomplete;
  @ViewChild('spinner', { static: true }) spinner: GridSpinnerComponent;
  private _placeholder: string;
  private _required = false;
  private _disabled = false;

  @HostBinding('attr.aria-describedby') describedBy = '';

  @Output() eventHandler: EventEmitter<any> = new EventEmitter();
  stateChanges: Subject<void> = new Subject<void>();
  @HostBinding() id = `infinite-scroll-input-${InfiniteScrollSelectComponent.nextId++}`;
  focused = false;
  errorState = false;
  controlType = 'infinite-scroll-input';

  private currentFilters: ResultFilter = new ResultFilter();
  protected totalResults = 0;
  private initialLoad = true;
  private clearResults = true;

  /** control for filter for server side. */
  @Input() searchControl: FormControl = new FormControl();

  /** indicate search operation is in progress */
  public searching = false;

  /** list of banks filtered after simulating server side search */
  public options = new BehaviorSubject<InfiniteScrollOption<T>[]>([]);
  public options$: Observable<InfiniteScrollOption<T>[]>;

  /** Subject that emits when the component has been destroyed. */
  protected _onDestroy = new Subject<void>();
  setDescribedByIds(ids: string[]): void {
    this.describedBy = ids.join(' ');
  }
  onContainerClick(event: MouseEvent) {
    if ((event.target as Element).tagName.toLowerCase() != 'input') {
      this.elRef.nativeElement.querySelector('input').focus();
    }
  }
  writeValue(obj: InfiniteScrollOption<T> | null): void {
    this.value = obj;
  }
  onChange = (_: any) => {};
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  onTouched = () => {};
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  ngOnInit() {
    this.options.next([new InfiniteScrollOption('Loading...', null, true, false, true)]);
    // listen for search field value changes
    this.searchControl.valueChanges.pipe(
      tap(() => this.searching = true),
      takeUntil(this._onDestroy),
      debounceTime(200),
      filter(q => typeof q === 'string'),
      map((search:string) => {
        if(search[search.length-1] != '*') {
          search += '*';
        }
        this.currentFilters = this.createResultsFilter(search);
        this.eventHandler.emit({state:'ONFILTER', filter: search});
        this.clearResults = true;
        this.options.next([new InfiniteScrollOption('Loading...', null, true, false, true)]);
        return this.searchFunction(this.currentFilters);
      })
    ).subscribe(filteredBanks => {
      filteredBanks.subscribe(
        results => {
          this.clearResults = true;
          this.mapSearchResults(results);
          this.searching = false;
          this.spinner.hideSpinner();
        });
    },
      error => {
        this.searching = false;
        this.spinner.hideSpinner();
        this.clearResults = true;
        this.options.next([new InfiniteScrollOption('Error retrieving results...', null, true, false, true)]);
      }
    );

    this.auto.opened.subscribe((opened) => {
      if(this.initialLoad) {
        this.initialLoad = false;
        this.clearResults = true;
        this.currentFilters = new ResultFilter();
        this.fetchBatch();
      } else {
        // to fix lazyload scroll when dropdown closed and opened again
        this.clearResults = true;
        this.currentFilters = new ResultFilter();
        this.reloadbatch();
      }
    });

    this.auto.optionSelected.subscribe((selection) => {
      this.eventHandler.emit({state:'ONSELECT', selection});
      this.onChange(selection.option.value);
    });
  }

  ngDoCheck(): void {
    if(this.ngControl) {
      this.errorState = (this.ngControl.invalid && this.ngControl.touched) || (this.searchControl.value && !(this.searchControl.value instanceof InfiniteScrollOption));
      this.stateChanges.next();
    }
  }

  displayWith(lookup) {
    let displayValue: string;
    if (Array.isArray(lookup) && lookup) {
      lookup.forEach((value, index) => {
        if (index === 0) {
          displayValue = value.description;
        } else {
          displayValue += ', ' + value.description;
        }
      });
    } else {
      displayValue = lookup ? lookup.description : null;
    }

    return displayValue;
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
    this.stateChanges.complete();
    this.fm.stopMonitoring(this.elRef.nativeElement);
  }

  getNextBatch() {
    if(this.hasNextBatch() && !this.searching) {
      this.currentFilters.startIndex += this.currentFilters.pageSize
      this.spinner.showSpinner()
      this.fetchBatch();
    }
  }

  fetchBatch() {
    this.searching = true;
    this.searchFunction(this.currentFilters).subscribe(results => {
      this.mapSearchResults(results);
      this.searching = false;
      this.spinner.hideSpinner();
    }, error => {
      this.searching = false;
      this.spinner.hideSpinner();
      this.clearResults = true;
      this.options.next([new InfiniteScrollOption('Error retrieving results...', null, true, false, true)]);
    });
  }

  hasNextBatch() {
    this.searching = false;
    return this.totalResults > (this.currentFilters.startIndex + this.currentFilters.pageSize);
  }

  reloadbatch() {
    this.currentFilters.startIndex = 0;
    this.spinner.showSpinner()
    this.fetchBatch();
  }

  abstract searchFunction(filter:ResultFilter) : Observable<Results<any>>;

  abstract mapSearchResults(results: Results<any>);

  abstract createResultsFilter(filter: string);
}

